import {
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  ChatIcon,
  CloseIcon,
  DownArrowIcon,
  EditListIcon,
  FullScreenIcon,
  GlobeCheckmarkIcon,
  LockIcon,
  // LogIcon,
  SearchIcon,
  TicketLogIcon,
  TimelineDrawer,
  Tooltip,
  UserIcon,
  ViewIcon,
} from "components";
import { useAtomValue } from "jotai";
import React from "react";
import type { AccessSpecifier } from "shared/types";
import { AssigneesBlock } from "./AssigneesBlock";
import { chatMembers } from "./store";

const NotesMenuList = [
  {
    title: "all",
    tagline: "View all conversations",
    icon: <ChatIcon fontSize="small" />,
  },
  {
    title: "private",
    tagline: "View all private conversations",
    icon: <LockIcon fontSize="small" />,
  },
  {
    title: "public",
    tagline: "View all public conversations",
    icon: <GlobeCheckmarkIcon fontSize="small" />,
  },

  {
    title: "self",
    tagline: "View all self conversations",
    icon: <UserIcon fontSize="small" />,
  },
  {
    title: "resolution",
    tagline: "View all Resolution conversations",
    icon: <EditListIcon fontSize="small" />,
  },
];

export const ChatHeader = ({
  onFilterSelect,
  lastUpdated: _lastUpdated,
  serviceType,
  serviceId,
  section,
  onClose,
  onFullscreenClick,
  onSearchClick,
  isDisabled,
}: {
  onFilterSelect?: (filter?: AccessSpecifier) => void;
  lastUpdated?: string;
  serviceType: string;
  serviceId: string;
  onClose: () => void;
  onFullscreenClick?: () => void;
  onSearchClick?: () => void;
  section: "serviceDetails" | "messageBar";
  isDisabled?: boolean;
}) => {
  const members = useAtomValue(chatMembers);
  const [isTimelineOpen, setIsTimelineOpen] = React.useState(false);
  const theme = useTheme();

  const [filterAccessSpecifier, setFilterAccessSpecifier] = React.useState<
    AccessSpecifier | undefined
  >();

  //
  const [notesAnchorEl, setNotesAnchorEl] = React.useState<null | HTMLElement>(
    null,
  );
  const open = Boolean(notesAnchorEl);
  const handleNotesMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setNotesAnchorEl(event.currentTarget);
  };

  const handleNotesMenuClose = () => {
    setNotesAnchorEl(null);
  };

  const handleNotesMenuOptionSelect = (
    type?: "public" | "private" | "self" | "all" | "resolution",
  ) => {
    // @ts-ignore
    setFilterAccessSpecifier(type);
    // @ts-ignore

    onFilterSelect && onFilterSelect(type);

    setNotesAnchorEl(null);
  };

  return (
    <>
      <Stack
        px="24px"
        height="48px"
        sx={{
          backgroundColor: "#FFF",
          flexShrink: 0,
          boxShadow: "0px 0px 4px 0px #0000000F,0px 0px 2px 0px #0000000A",
        }}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        {/* header */}
        <Stack direction="row" gap="8px">
          {members.length > 0 && (
            <AssigneesBlock
              members={members}
              serviceId={serviceId}
              isDisabled={isDisabled}
              serviceType={serviceType}
            />
          )}

          {/* <Stack
            sx={{
              justifyContent: "center",
            }}
          >
            <Typography
              maxWidth="200px"
              variant="subtitle1"
              noWrap
              textOverflow="ellipsis"
              sx={{ textTransform: "capitalize" }}
            >
              {getMemberNames()}
            </Typography>
            {lastUpdated && (
              <Typography variant="caption-3" color={theme.palette.lightBg.low}>
                Last updated {dayjs(lastUpdated).fromNow()}
              </Typography>
            )}
          </Stack> */}
        </Stack>

        {/* icons */}
        <Stack direction="row" gap="12px" alignItems={"center"}>
          {/* search button */}
          {section === "serviceDetails" && (
            <Tooltip title="Search" placement="top">
              <IconButton
                aria-label="Search in chat"
                onClick={onSearchClick}
                sx={{
                  padding: "0px",
                  ":hover": {
                    backgroundColor: "transparent",
                  },
                }}
              >
                <SearchIcon
                  htmlColor={theme.palette.lightBg.high}
                  sx={{ width: "20px", height: "20px", fontSize: "20px" }}
                />
              </IconButton>
            </Tooltip>
          )}
          {/* ticket timeline button */}
          {section === "serviceDetails" && (
            <Tooltip title="Ticket timeline" placement="top">
              <IconButton
                aria-label="View timeline"
                onClick={() => setIsTimelineOpen(true)}
                sx={{
                  padding: "0px",
                  ":hover": {
                    backgroundColor: "transparent",
                  },
                }}
              >
                <TicketLogIcon
                  htmlColor={theme.palette.lightBg.high}
                  sx={{ width: "20px", height: "20px", fontSize: "20px" }}
                />
              </IconButton>
            </Tooltip>
          )}
          {section === "messageBar" && (
            <IconButton onClick={onFullscreenClick}>
              <FullScreenIcon htmlColor={theme.palette.lightBg.high} />
            </IconButton>
          )}
          {/* conversation filter button */}
          {serviceType === "ticket" && (
            <>
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  height: "16px",
                  alignSelf: "center",
                  borderColor: theme.palette.neutral["020"],
                }}
              />
              <Tooltip title="View Conversations" placement="top">
                <IconButton
                  id="notes-button"
                  aria-controls={open ? "notes-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? "true" : undefined}
                  onClick={handleNotesMenuClick}
                  sx={{
                    minWidth: "82px",
                    width: "fit-content",
                    height: "32px",
                    borderRadius: "4px",
                    padding: "8px",
                    color: theme.palette.lightBg.high,
                    marginLeft: "-4px",
                    "&:hover": {
                      color: theme.palette.purple.main,
                      "& .MuiTypography-root": {
                        color: theme.palette.purple.main,
                      },
                      "& .MuiSvgIcon-root": {
                        color: theme.palette.purple.main,
                      },
                    },
                  }}
                >
                  <Stack
                    direction={"row"}
                    gap={"4px"}
                    justifyContent={"space-between"}
                    alignItems="center"
                    sx={{ width: "100%" }}
                  >
                    <Stack direction={"row"} gap={"6px"} alignItems={"center"}>
                      <ViewIcon
                        sx={{ width: "16px", height: "16px", fontSize: "16px" }}
                      />
                      <Typography
                        color={theme.palette.lightBg.high}
                        variant="body-small"
                        sx={{ textTransform: "capitalize" }}
                      >
                        {filterAccessSpecifier ? filterAccessSpecifier : "all"}
                      </Typography>
                    </Stack>
                    <DownArrowIcon fontSize="small" />
                  </Stack>
                </IconButton>
              </Tooltip>
              <Menu
                id="notes-menu"
                anchorEl={notesAnchorEl}
                open={open}
                onClose={handleNotesMenuClose}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                MenuListProps={{
                  "aria-labelledby": "notes-button",
                }}
              >
                {NotesMenuList?.map((menu) => (
                  <Box sx={{ minWidth: "260px" }} key={menu.title}>
                    <MenuItem
                      sx={{
                        my: "6px",
                        mx: "12px",
                        py: "8px",
                        px: "10px",
                        textTransform: "capitalize",
                        border: `1px solid ${theme.palette.neutral["005"]}`,
                        borderRadius: "8px",
                      }}
                      key={menu.title}
                      selected={menu.title === filterAccessSpecifier}
                      onClick={() =>
                        handleNotesMenuOptionSelect(
                          // @ts-ignore
                          menu.title,
                        )
                      }
                    >
                      <Stack
                        direction={"row"}
                        gap={"12px"}
                        alignItems={"center"}
                      >
                        <Box
                          sx={{
                            width: "32px",
                            height: "32px",
                            backgroundColor: "#E7E9FE",
                            borderRadius: "100%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            color: theme.palette.purple.main,
                          }}
                        >
                          {menu.icon}
                        </Box>
                        <Stack>
                          <Typography variant="body-medium">
                            {menu.title}
                          </Typography>
                          <Typography
                            variant="body-small"
                            color={theme.palette.lightBg.low}
                          >
                            {menu.tagline}
                          </Typography>
                        </Stack>
                      </Stack>
                    </MenuItem>
                    {/* {index < NotesMenuList.length - 1 && <Divider />} */}
                  </Box>
                ))}
              </Menu>
            </>
          )}
          {section === "messageBar" && (
            <IconButton onClick={onClose}>
              <CloseIcon htmlColor={theme.palette.lightBg.high} />
            </IconButton>
          )}
        </Stack>
      </Stack>
      {section === "serviceDetails" && isTimelineOpen && (
        <TimelineDrawer
          serviceId={serviceId}
          serviceType={serviceType as "ticket" | "project"}
          onClose={() => setIsTimelineOpen(false)}
          open={isTimelineOpen}
        />
      )}
    </>
  );
};
